import type { ReactNode } from "react";
import type { Workflow, WorkflowSampleResponse } from "@mlx/contracts";
import { Alert } from "@/components/ui/alert";
import { NativeSelect } from "@/components/ui/native-select";
import { formatElapsedAgo, formatStepDuration } from "@/lib/format";

/*
 * The Results tab as markup: the sample's status and notices, the step
 * picker, and the chosen step's output. WorkflowResults keeps the chosen step
 * and renders each step's output table (WorkflowStepOutput) into `output`; a
 * fixture sample passes its own table there, so it composes the same tab.
 */

/** The latest sample, as the Results tab reads it. */
type ResultsRun = {
  workflow: Workflow;
  /** A run of the session is in progress. */
  running: boolean;
  latestRunId?: string;
  sample?: WorkflowSampleResponse;
  /** The clock a recorded sample's age is read against; the current time when omitted. */
  now?: number;
};
type WorkflowNode = Workflow["definition"]["nodes"][number];

function runDuration(nodes: WorkflowSampleResponse["nodes"]) {
  const ends = nodes.flatMap((node) => (node.completedAt ? [node.completedAt] : []));
  return ends.length ? Math.max(...ends) - Math.min(...nodes.map((node) => node.startedAt)) : undefined;
}
function failedAt(workflow: Workflow, sample: WorkflowSampleResponse) {
  const failed = sample.nodes.find((node) => node.status === "failed");
  const label = failed && workflow.definition.nodes.find((node) => node.id === failed.nodeId)?.label;
  return label ? ` at ${label}` : "";
}
/** The recorded result predates the run in progress, or the latest run recorded none. */
function showsPrevious({ workflow, running, latestRunId }: ResultsRun) {
  return Boolean(
    workflow.preview &&
    (running || latestRunId) &&
    (!latestRunId || workflow.previewRun?.id !== latestRunId),
  );
}

/**
 * The steps the Results tab can show: the workflow's own outputs, which lead
 * and are the default, then every other step with a recorded output.
 *
 * The outputs lead with what a scheduled run posts: the steps marked as
 * output, or the last step when none is marked. The steps no other step reads
 * follow. A step marked as output stays one when a later step reads it (a
 * count of its rows, say), so the tab opens on the table rather than on the
 * count.
 */
export function workflowResultSteps(workflow: Workflow): { outputs: WorkflowNode[]; intermediate: WorkflowNode[] } {
  const { nodes } = workflow.definition;
  const consumed = new Set(nodes.flatMap((node) => node.inputs));
  const marked = nodes.filter((node) => node.kind === "output");
  const posted = marked.length ? marked : nodes.slice(-1);
  const outputs = [
    ...posted,
    ...nodes.filter((node) => !posted.includes(node) && !consumed.has(node.id)),
  ];
  const shown = new Set(outputs.map((node) => node.id));
  const recordedSteps = new Set(workflow.preview?.nodes.map((node) => node.id));
  const intermediate = nodes.filter(
    (node) => !shown.has(node.id) && recordedSteps.has(node.id),
  );
  return { outputs, intermediate };
}

/** When a previous result was recorded, shown beside notices about it. */
function RecordedAt({ workflow }: { workflow: Workflow }) {
  if (!workflow.previewRun) return null;
  return (
    <time dateTime={new Date(workflow.previewRun.createdAt).toISOString()}>
      {new Date(workflow.previewRun.createdAt).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "medium",
      })}
    </time>
  );
}

/**
 * The notices over the result: a run in progress, a result from an earlier
 * run or version, and a failed or cancelled sample.
 */
export function WorkflowResultsNotices(props: ResultsRun) {
  const { workflow, running, sample, now } = props;
  const previous = showsPrevious(props);
  const recorded = previous && workflow.previewRun ? <RecordedAt workflow={workflow} /> : null;
  return (
    <>
      {running ? (
        <p role="status" className="text-sm text-muted-foreground">
          {previous || !workflow.preview ? (
            <>
              Run in progress. Waiting for a new sample result
              {recorded ? <>; the result below was recorded {recorded}</> : null}
              .
            </>
          ) : (
            "Sample result recorded. The run is finishing."
          )}
        </p>
      ) : previous ? (
        <Alert>
          The latest run has not recorded a new sample. Showing the result
          {recorded ? <> recorded {recorded}</> : null}.
        </Alert>
      ) : null}
      {sample?.status === "cancelled" || sample?.status === "failed" ? (
        <Alert variant="danger">
          {sample.status === "cancelled"
            ? "Sample cancelled."
            : `Sample failed${failedAt(workflow, sample)}.`}{" "}
          {sample.error}
        </Alert>
      ) : null}
      {workflow.preview &&
      workflow.preview.version !== workflow.version ? (
        <Alert>
          This output belongs to workflow version {workflow.preview.version}.
        </Alert>
      ) : null}
      {workflow.preview?.error && sample?.status !== "failed" ? (
        <Alert variant="danger">
          The last sample failed
          {workflow.previewRun ? ` ${formatElapsedAgo(workflow.previewRun.createdAt, now)}` : ""}:{" "}
          {workflow.preview.error}
        </Alert>
      ) : null}
    </>
  );
}

/** The result's status line: previous or not, how the sample ended, when, how long it took, and the version. */
export function workflowResultsStatus(props: ResultsRun): string {
  const { workflow, sample, now } = props;
  const outcome =
    sample?.runId === workflow.previewRun?.id
      ? sample?.status
      : workflow.preview?.status;
  const traced =
    sample && sample.runId === workflow.previewRun?.id && sample.nodes.length
      ? runDuration(sample.nodes)
      : undefined;
  return [
    showsPrevious(props) ? "Previous sample" : null,
    outcome === "cancelled"
      ? "Sample cancelled"
      : outcome === "succeeded"
        ? "Sample completed"
        : outcome === "failed"
          ? "Sample failed"
          : "Sample result recorded",
    workflow.previewRun ? formatElapsedAgo(workflow.previewRun.createdAt, now) : null,
    traced !== undefined ? `took ${formatStepDuration(traced)}` : null,
    workflow.preview ? `v${workflow.preview.version}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Picks the step whose output the tab shows: the workflow's outputs, then the other steps. */
export function WorkflowResultStepPicker({
  outputs,
  intermediate,
  value,
  onChange,
}: {
  outputs: WorkflowNode[];
  intermediate: WorkflowNode[];
  value: string;
  onChange?: (nodeId: string) => void;
}) {
  return (
    <NativeSelect
      aria-label="Result step"
      value={value}
      onChange={onChange && ((event) => onChange(event.target.value))}
      className="w-auto"
    >
      <optgroup label="Workflow output">
        {outputs.map((node) => (
          <option key={node.id} value={node.id}>
            {node.label}
          </option>
        ))}
      </optgroup>
      {intermediate.length ? (
        <optgroup label="Other steps">
          {intermediate.map((node) => (
            <option key={node.id} value={node.id}>
              {node.label}
            </option>
          ))}
        </optgroup>
      ) : null}
    </NativeSelect>
  );
}

/**
 * The latest sample's result: the chosen step's output (the workflow's own
 * output until another step is picked), headed by the step picker and the
 * sample's status, with notices while a new sample runs or after one failed.
 */
export function WorkflowResultsView({
  selectedId = "",
  onSelectStep,
  output,
  ...run
}: ResultsRun & {
  /** The step picked in the Result step menu; none shows the workflow's first output. */
  selectedId?: string;
  onSelectStep?: (nodeId: string) => void;
  /** The chosen step's output table, with the tab's picker and status as its leading controls and the notices above it. */
  output: (step: { nodeId: string; leading: ReactNode; notices: ReactNode }) => ReactNode;
}) {
  const { workflow } = run;
  const { outputs, intermediate } = workflowResultSteps(workflow);
  const steps = [...outputs, ...intermediate];
  const selected = steps.find((node) => node.id === selectedId) ?? outputs[0];
  const notices = <WorkflowResultsNotices {...run} />;
  if (!workflow.preview)
    return (
      <div className="space-y-3 p-4">
        {notices}
        <p className="text-sm text-muted-foreground">
          No completed sample run yet. Test a sample to see node outputs and any
          failures here.
        </p>
      </div>
    );
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {selected
        ? output({
            nodeId: selected.id,
            leading: (
              <>
                {steps.length > 1 ? (
                  <WorkflowResultStepPicker outputs={outputs} intermediate={intermediate}
                    value={selected.id} onChange={onSelectStep} />
                ) : null}
                <span className="text-xs text-muted-foreground">{workflowResultsStatus(run)}</span>
              </>
            ),
            notices,
          })
        : null}
    </div>
  );
}
